import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { PhoneFrame } from '../PhoneFrame';
import { Sparkle } from '../Sparkle';
import { TryFreeLink } from '../TryFreeLink';
import { dinoLesson, foxCutout } from '../../lib/brandAssets';
import { featuredLesson } from '../../lib/appAssets';
import { showcaseCards, showcaseQuestion } from '../../data/dinoShowcase';
const BOLT_PATH = 'M58 2L14 58H40L30 98L86 36H58L70 2Z';

/** A peek inside the real Dinosaur Days adventure: clues, question, badge. */
export function HeroBanner() {
  const [active, setActive] = useState(0);
  const reduceMotion = useReducedMotion();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const card = showcaseCards[active];
  const selectTab = (next: number) => {
    const index = (next + showcaseCards.length) % showcaseCards.length;
    setActive(index);
    tabRefs.current[index]?.focus();
  };
  const onTabKeyDown = (event: React.KeyboardEvent) => {
    const keys: Record<string, number> = {
      ArrowRight: active + 1,
      ArrowLeft: active - 1,
      Home: 0,
      End: showcaseCards.length - 1
    };
    if (event.key in keys) {
      event.preventDefault();
      selectTab(keys[event.key]);
    }
  };
  const fade = reduceMotion ? {
    initial: false as const,
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 1
    },
    transition: {
      duration: 0
    }
  } : {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.18,
      ease: [0.23, 1, 0.32, 1] as const
    }
  };
  return <figure aria-labelledby="showcase-title" className="relative -mx-3 mt-6 overflow-hidden rounded-[28px] bg-lavender px-4 pb-6 pt-7 sm:mx-0 sm:rounded-[36px] sm:px-8 sm:pb-8 sm:pt-9 lg:px-12" data-id="element-175" data-mp="12ckrbm6ec6ge">
      <svg viewBox="0 0 100 100" aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[90%] w-[90%] -translate-x-1/2 -translate-y-1/2" data-id="element-176" data-mp="9p85je8282o5">
        <path d={BOLT_PATH} fill="#E6E0FD" data-id="element-177" data-mp="6m2a4yi2f5yg" />
      </svg>
      <Sparkle className="absolute right-[5%] top-6 h-5 w-5" drift={9} delay={1.2} data-id="element-178" data-mp="1pbwehtqgmg7" />
      <Sparkle className="absolute left-[4%] top-[45%] hidden h-5 w-5 md:block" drift={8} data-id="element-179" data-mp="gdf3jujii46l" />

      <div className="relative" data-id="element-180" data-mp="belldota5p03">
        {/* Heading */}
        <div className="text-center" data-id="element-181" data-mp="bf3rre9npjlq">
          <h2 id="showcase-title" className="font-display text-[32px] leading-[1.05] tracking-tight text-ink [text-wrap:balance] sm:text-[42px] lg:text-[48px]" data-id="element-182" data-mp="3juf6kgyk3onh">
            A peek inside an adventure
          </h2>
          <p className="mx-auto mt-3 max-w-2xl font-display text-[19px] leading-snug text-ink/80 [text-wrap:balance] sm:text-[22px]" data-id="element-183" data-mp="3ttcov25e2ly1">
            <span className="sr-only" data-id="element-184" data-mp="3ovuqg5ozzp4a">Lesson: </span>
            “{dinoLesson.title}”
          </p>
        </div>

        {/* Two stages */}
        <div className="mx-auto mt-8 grid max-w-[880px] items-start gap-10 md:grid-cols-2 md:gap-8 lg:gap-12" data-id="element-185" data-mp="1442aodllsps3">
          {/* 1. Discover the clues */}
          <div className="mx-auto w-full max-w-[350px]" data-id="element-186" data-mp="1bov488ivzjo7">
            <StageLabel number="1" title="Discover the clues" data-id="element-187" data-mp="1gmwj0h9a4wtv" />

            <div role="tablist" aria-label="Cards from the adventure" onKeyDown={onTabKeyDown} className="mt-3 grid grid-cols-3 gap-1 rounded-2xl bg-white/70 p-1" data-id="element-188" data-mp="12mdevl7xqawz">
              {showcaseCards.map((item, i) => {
              const selected = i === active;
              return <button key={item.id} ref={el => {
                tabRefs.current[i] = el;
              }} id={`showcase-tab-${item.id}`} type="button" role="tab" aria-selected={selected} aria-controls="showcase-panel" tabIndex={selected ? 0 : -1} onClick={() => setActive(i)} className={`whitespace-nowrap rounded-xl px-2 py-2.5 text-[15px] font-bold transition-colors duration-150 ease-out sm:text-[16px] ${selected ? 'bg-ink text-cream' : 'text-ink hover:bg-white'}`} data-id="element-189" data-mp="1zbl2qzw10w42" data-mpi={i}>
                    {item.tab}
                  </button>;
            })}
            </div>
            <p className="mt-2 text-center text-[14px] text-ink/70" data-id="element-190" data-mp="1d5a7bz56qyqe">Three cards from the adventure</p>

            <PhoneFrame aspectClassName="" className="mt-4 md:-rotate-[0.5deg]" data-id="element-191" data-mp="185nvnu509qjp">
              <div id="showcase-panel" role="tabpanel" aria-labelledby={`showcase-tab-${card.id}`} className="px-4 pb-5 pt-9" data-id="element-192" data-mp="13ez4kfx8mvl3">
                <AnimatePresence mode="wait" initial={false} data-id="element-193" data-mp="1vw1q1rb2c1hg">
                  <motion.div key={card.id} {...fade} data-id="element-194" data-mp="1vxmgb4qcgy9i">
                    <div className="rounded-2xl bg-cream-field p-1.5" data-id="element-195" data-mp="1r3qn0fl0zzko">
                      <img src={card.src} alt={card.alt} width={card.width} height={card.height} className="aspect-square w-full object-contain" data-id="element-196" data-mp="39rtbyawz5hum" />
                    </div>
                    <p className="mt-4 font-display text-[17px] leading-snug text-ink md:text-[18px]" data-id="element-197" data-mp="3nrkt2kisfecu">{card.text}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </PhoneFrame>
          </div>

          {/* 2. Try a question: a static answer example, not interactive */}
          <div className="mx-auto w-full max-w-[350px]" data-id="element-198" data-mp="6c4bjkzn2z39">
            <StageLabel number="2" title="Try a question" data-id="element-199" data-mp="ac6eryl0w0kn" />

            <PhoneFrame aspectClassName="" screenClassName="bg-lavender-soft" className="mt-3 md:rotate-[0.5deg]" data-id="element-200" data-mp="abmuwcbtb23z">
              <div className="px-4 pb-5 pt-9" data-id="element-201" data-mp="f9odkw5bszex">
                <p className="text-[13px] font-extrabold uppercase tracking-[0.1em] text-ink/70" data-id="element-202" data-mp="k9stajyetq43">Quick question</p>
                <p className="mt-1.5 font-display text-[19px] leading-snug text-ink" data-id="element-203" data-mp="2geij60ayg9h9">{showcaseQuestion.question}</p>

                <ul className="mt-4 space-y-2" aria-label="Answer choices" data-id="element-204" data-mp="2bggzbd5pg64q">
                  {showcaseQuestion.options.map((option, i) => {
                  const correct = i === showcaseQuestion.correctIndex;
                  return <li key={option} className={`flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-[16px] leading-snug text-ink ${correct ? 'bg-teal/15 font-bold ring-2 ring-teal' : 'bg-white/80'}`} data-id="element-205" data-mp="2z3p3frred956" data-mpi={i}>
                        <span aria-hidden="true" className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${correct ? 'bg-teal text-white' : 'border-2 border-ink/20'}`} data-id="element-206" data-mp="344mk1zwrdv4k">
                          {correct && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} data-id="element-207" data-mp="3lezwpvlkvq8m" />}
                        </span>
                        <span className="min-w-0" data-id="element-208" data-mp="3bmu4ckrez6sw">
                          {option}
                          {correct && <span className="mt-0.5 block text-[13px] font-extrabold" style={{
                        color: '#147A5B'
                      }} data-id="element-209" data-mp="3gl5e6vhplhb4">
                              Answer shown
                            </span>}
                        </span>
                      </li>;
                })}
                </ul>

                <p className="mt-4 text-[16px] text-ink" data-id="element-210" data-mp="2spszczxv16oy">{showcaseQuestion.explanation}</p>
              </div>
            </PhoneFrame>
            <p className="mt-3 text-center text-[13px] font-semibold text-ink/70" data-id="element-211" data-mp="qwlihm2lmh0y">Illustrative lesson preview</p>
          </div>
        </div>

        {/* 3. Earn a badge to keep */}
        <div className="relative mx-auto mt-10 flex max-w-[880px] flex-col items-center gap-4 rounded-3xl bg-white/80 p-4 text-center sm:p-5 lg:flex-row lg:gap-5 lg:pr-[124px] lg:text-left" data-id="element-212" data-mp="vtc0wg1vlopj">
          <div className="w-[96px] shrink-0" data-id="element-213" data-mp="3auqomhkzfiou">
            <img src={dinoLesson.badge.src} alt={dinoLesson.badge.alt} loading="lazy" decoding="async" width={dinoLesson.badge.width} height={dinoLesson.badge.height} className="aspect-square w-full object-contain" data-id="element-214" data-mp="3615pgm7nkio2" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-215" data-mp="2s0uxtgyir01u">
            <StageLabel number="3" title="Earn a badge to keep" align="left" data-id="element-216" data-mp="2n2jmt2whmji1" />
            <p className="mt-1 text-[16px] text-ink" data-id="element-217" data-mp="2i3ylf05i1ts0">
              <span className="font-bold" data-id="element-218" data-mp="2i59yxroqyfwb">{dinoLesson.badge.name}.</span> Every completed adventure earns a
              downloadable badge.
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-center gap-1.5" data-id="element-219" data-mp="gc3m7k0fjhqo">
            <TryFreeLink size="sm" data-id="element-220" data-mp="nw4k64448dwi" />
            <p className="max-w-[13rem] text-[13px] leading-snug text-ink/70" data-id="element-221" data-mp="nv37jv7gbjco">
              Free sample: {featuredLesson.title}
            </p>
          </div>
          <img src={foxCutout.src} alt="" loading="lazy" decoding="async" width={foxCutout.width} height={foxCutout.height} className="pointer-events-none absolute -bottom-3 right-2 hidden w-[104px] object-contain lg:block" data-id="element-222" data-mp="xlxuz2wfoozp" />
        </div>
      </div>
    </figure>;
}
function StageLabel({
  number,
  title,
  align = 'center'
}: {
  number: string;
  title: string;
  align?: 'center' | 'left';
}) {
  return <p className={`relative flex items-center gap-2 font-display text-[19px] text-ink ${align === 'center' ? 'justify-center' : 'justify-center lg:justify-start'}`} data-id="element-223" data-mp="exsv46iwdo66">
      <span aria-hidden="true" className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sun text-[15px] ring-2 ring-ink" data-id="element-224" data-mp="1qcxzrg2l9wsf">
        {number}
      </span>
      <span data-id="element-225" data-mp="pso870at2f4u">
        <span className="sr-only" data-id="element-226" data-mp="twcgxmb6bgro">Step {number}: </span>
        {title}
      </span>
    </p>;
}